// Render parsed book chapters into styled HTML for preview and EPUB/PDF export.

import type { ParsedBook, BookChapter } from './book-parser';
import { bookTitle, bookSubtitle } from './book-parser';
import { REGISTER_TAGS, resolveTagLabel } from '@glossikatech/register-tags';
import { GLOSSIKA_LOGO } from './glossika-logo';

const GLOSSIKA_LOGO_SVG = `<svg viewBox="0 0 200 40" xmlns="http://www.w3.org/2000/svg">
  <text x="0" y="30" font-family="'Cormorant Garamond', Georgia, serif" font-size="28" font-weight="700" fill="currentColor">GLOSSIKA</text>
</svg>`;

function escapeHtml(text: string): string {
  return text.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;');
}

const RTL_SCRIPTS = /[؀-ۿݐ-ݿࢠ-ࣿﭐ-﷿ﹰ-﻿֐-׿יִ-ﭏ]/;

// A maximal run of RTL-script text plus neutrals BETWEEN rtl chars (internal spaces/punctuation) — not
// trailing neutrals, not Latin letters/digits, never across an HTML tag (<>). Built from the same ranges
// as RTL_SCRIPTS so the two never drift.
const RTL_CLASS = RTL_SCRIPTS.source.slice(1, -1);
const RTL_RUN = new RegExp(`[${RTL_CLASS}]+(?:[^${RTL_CLASS}A-Za-z0-9<>]+[${RTL_CLASS}]+)*`, 'g');

// Isolate each embedded RTL run in a <bdi> so that inside English (LTR) prose a Hebrew/Arabic word is laid
// out right-to-left ON ITS OWN, and the surrounding punctuation and word order stay correct — no periods or
// commas jumping onto the neighbouring word, no clause getting reversed.
function bidiIsolate(html: string): string {
  return html.replace(RTL_RUN, (m) => `<bdi>${m}</bdi>`);
}

// Base direction "auto" (from the first strong character) + isolate embedded RTL runs: an English-primary
// line reads left-to-right with Hebrew embedded RTL; a Hebrew-primary line reads right-to-left with Latin
// embedded LTR. (Previously this wrapped the WHOLE text in dir="rtl" on ANY rtl char, which forced English
// prose to render right-to-left and scrambled its punctuation — the reported book bug.)
function wrapDir(text: string): string {
  if (!RTL_SCRIPTS.test(text)) return text;
  return `<span dir="auto">${bidiIsolate(text)}</span>`;
}

function renderRegisterBadge(tag: string, baseLang?: string): string {
  const t = REGISTER_TAGS[tag];
  if (!t) return `<code class="book-code">[${escapeHtml(tag)}]</code>`;
  const label = resolveTagLabel(tag, baseLang);
  return `<span class="register-tag" style="background:${t.color}20;color:${t.color};border:1px solid ${t.color}40;">${escapeHtml(label)}</span>`;
}

function renderInlineBook(text: string, baseLang?: string): string {
  let result = escapeHtml(text);
  result = result.replace(/\*\*(.+?)\*\*/g, '<strong>$1</strong>');
  result = result.replace(/\*(.+?)\*/g, '<em>$1</em>');
  result = result.replace(/`\[([A-Z]{2,4})\]`/g, (_m, tag) => renderRegisterBadge(tag, baseLang));
  // Bare register/gloss/rom tags that leaked into prose, list items or orphaned gloss lines (not caught
  // by the sentence-block/turn structure) still become chips instead of literal "[GLO]"/"[STD]" text.
  // Unknown bracketed tokens (e.g. [SOV], [SVO]) are left untouched.
  result = result.replace(/\[([A-Z]{2,4})\]/g, (_m, tag) => {
    if (tag === 'GLO') return '<span class="gloss-tag">GLOSS</span>';
    if (tag === 'ROM') return '<span class="rom-tag">ROM</span>';
    if (REGISTER_TAGS[tag]) return renderRegisterBadge(tag, baseLang);
    return _m;
  });
  result = result.replace(/`([^`]+)`/g, '<code class="book-code">$1</code>');
  return bidiIsolate(result);   // isolate embedded RTL (Hebrew/Arabic) runs so mixed text orders correctly
}

function renderTableBook(tableLines: string[], baseLang?: string): string {
  if (tableLines.length < 2) return '';
  const parseCells = (line: string): string[] =>
    line.replace(/^\|/, '').replace(/\|$/, '').split('|').map(c => c.trim());

  const headerCells = parseCells(tableLines[0]!);
  const bodyRows: string[][] = [];
  for (let i = 2; i < tableLines.length; i++) {
    if (tableLines[i]) bodyRows.push(parseCells(tableLines[i]!));
  }

  let html = '<table class="book-table"><thead><tr>';
  for (const cell of headerCells) html += `<th dir="auto">${renderInlineBook(cell, baseLang)}</th>`;
  html += '</tr></thead><tbody>';
  for (const row of bodyRows) {
    html += '<tr>';
    for (let ci = 0; ci < Math.max(row.length, headerCells.length); ci++) {
      html += `<td dir="auto">${renderInlineBook(row[ci] ?? '', baseLang)}</td>`;
    }
    html += '</tr>';
  }
  html += '</tbody></table>';
  return html;
}

function isTableRow(line: string): boolean {
  const t = line.trim();
  return t.startsWith('|') && t.endsWith('|') && t.includes('|');
}

function isTableSep(line: string): boolean {
  return /^\|[\s\-:|]+\|$/.test(line.trim()) || /^[\s\-:|]+\|[\s\-:|]+/.test(line.trim());
}

function renderMarkdownToBookHtml(md: string, baseLang?: string): string {
  const lines = md.split('\n');
  const blocks: string[] = [];
  let i = 0;

  while (i < lines.length) {
    const line = lines[i] ?? '';
    const trimmed = line.trim();

    if (trimmed === '') { i++; continue; }

    if (/^-{3,}$/.test(trimmed) || /^\*{3,}$/.test(trimmed)) {
      blocks.push('<hr class="book-hr" />');
      i++; continue;
    }

    // Skip ## and ### headers (handled by chapter/section structure)
    if (/^#{1,3}\s/.test(trimmed)) { i++; continue; }

    // #### subheaders
    if (/^####\s/.test(trimmed)) {
      const text = trimmed.replace(/^####\s+/, '');
      blocks.push(`<h4 class="book-h4">${renderInlineBook(text, baseLang)}</h4>`);
      i++; continue;
    }

    // Any OTHER line starting with "#" (a stray/lone "#", "#####", "#word" that is NOT a recognized
    // heading) must still advance i. Otherwise it falls to the paragraph loop below, whose first guard
    // `pl.startsWith('#')` breaks immediately WITHOUT i++, so the outer while spins forever — a doc with
    // lone "#" lines hangs the entire render. Skip it.
    if (trimmed.startsWith('#')) { i++; continue; }

    // Blockquote (mini-dialogs)
    if (trimmed.startsWith('>')) {
      const quoteLines: string[] = [];
      while (i < lines.length && (lines[i]?.trim().startsWith('>') || lines[i]?.trim() === '')) {
        const ql = lines[i]?.trim() ?? '';
        if (ql === '' && quoteLines.length > 0) { quoteLines.push(''); i++; continue; }
        if (!ql.startsWith('>') && ql !== '') break;
        quoteLines.push(ql.replace(/^>\s?/, ''));
        i++;
      }
      blocks.push(`<blockquote class="book-dialog">${quoteLines.map(l => l ? `<p>${renderInlineBook(l, baseLang)}</p>` : '').join('\n')}</blockquote>`);
      continue;
    }

    // Table
    if (isTableRow(trimmed)) {
      const tableLines: string[] = [];
      while (i < lines.length) {
        const tl = (lines[i] ?? '').trim();
        if (isTableRow(tl) || isTableSep(tl)) { tableLines.push(tl); i++; }
        else break;
      }
      if (tableLines.length >= 2) blocks.push(renderTableBook(tableLines, baseLang));
      continue;
    }

    // Unordered list
    if (/^[-*+]\s/.test(trimmed)) {
      const items: string[] = [];
      while (i < lines.length && /^[-*+]\s/.test((lines[i] ?? '').trim())) {
        items.push((lines[i] ?? '').trim().replace(/^[-*+]\s/, ''));
        i++;
      }
      blocks.push(`<ul class="book-ul">${items.map(it => `<li>${renderInlineBook(it, baseLang)}</li>`).join('\n')}</ul>`);
      continue;
    }

    // Ordered list
    if (/^\d+\.\s/.test(trimmed)) {
      const items: string[] = [];
      while (i < lines.length && /^\d+\.\s/.test((lines[i] ?? '').trim())) {
        items.push((lines[i] ?? '').trim().replace(/^\d+\.\s/, ''));
        i++;
      }
      blocks.push(`<ol class="book-ol">${items.map(it => `<li>${renderInlineBook(it, baseLang)}</li>`).join('\n')}</ol>`);
      continue;
    }

    // Sentence block: register tag (alone or with inline content)
    // Formats: `[STD]`  |  [STD]  |  [STD] target sentence...
    const tagMatch = trimmed.match(/^`?\[([A-Z]{2,4})\]`?(?:\s+(.*))?$/);
    if (tagMatch && REGISTER_TAGS[tagMatch[1]!] && tagMatch[1] !== 'ROM' && tagMatch[1] !== 'GLO') {
      const tag = tagMatch[1]!;
      const badge = renderRegisterBadge(tag, baseLang);
      const inlineContent = (tagMatch[2] ?? '').trim();

      const rawLines: string[] = [];
      // The box = the target sentence (+ any [ROM]/[GLO]) + its English translation, and it ENDS at that
      // translation. The translation is EITHER inline on the target line ("target — English") OR on its own
      // line, possibly after one blank ("[STD] target" / blank / "English"). Once the translation is in, the
      // box closes — a following grammar note or the next example stays OUTSIDE.
      // "Inline English" = a " — " whose tail is Latin text with no CJK/Hangul, so a Japanese/Greek/Cyrillic
      // target that merely contains an em-dash is NOT mistaken for carrying an inline English translation.
      const inlineEnglish = (s: string): boolean => {
        const parts = s.split(/\s[—–]\s/);
        if (parts.length < 2) return false;
        const tail = parts[parts.length - 1]!;
        return /[A-Za-z]/.test(tail) && !/[぀-ヿ㐀-鿿가-힯]/.test(tail);
      };
      // Content is normalized so no example joins its English onto the target line with " — "; the
      // translation is always its own line. (inlineEnglish retained but unused — the normalization made
      // its job obsolete.) void it so linters don't flag it.
      void inlineEnglish;
      if (inlineContent) rawLines.push(inlineContent);
      i++;
      while (i < lines.length) {
        const sl = (lines[i] ?? '').trim();
        if (sl === '') break;   // examples are normalized into blank-delimited blocks; a blank ends the box
        const nextTag = sl.match(/^`?\[([A-Z]{2,4})\]`?/);
        if (nextTag && REGISTER_TAGS[nextTag[1]!] && nextTag[1] !== 'ROM' && nextTag[1] !== 'GLO') break;
        if (sl.startsWith('#') || sl.startsWith('>') || isTableRow(sl)) break;
        rawLines.push(sl); i++;   // collect every line of the block (target, [ROM]/[GLO], English) until the blank
      }

      if (rawLines.length > 0) {
        const parts: { type: string; text: string }[] = [];
        for (const raw of rawLines) {
          if (/\[(?:ROM|GLO)\]/.test(raw) && !raw.startsWith('[ROM]') && !raw.startsWith('[GLO]')) {
            const segs = raw.split(/\[(ROM|GLO)\]/);
            let curType = parts.length === 0 ? 'target' : 'translation';
            for (const seg of segs) {
              if (seg === 'ROM') { curType = 'rom'; continue; }
              if (seg === 'GLO') { curType = 'glo'; continue; }
              const t = seg.trim();
              if (t) parts.push({ type: curType, text: t });
            }
          } else if (raw.startsWith('[ROM]')) {
            const t = raw.replace(/^\[ROM\]\s*/, '').trim();
            if (t) parts.push({ type: 'rom', text: t });
          } else if (raw.startsWith('[GLO]')) {
            const t = raw.replace(/^\[GLO\]\s*/, '').trim();
            if (t) parts.push({ type: 'glo', text: t });
          } else if (raw.startsWith('/') && raw.endsWith('/')) {
            parts.push({ type: 'ipa', text: raw });
          } else if (parts.length === 0) {
            parts.push({ type: 'target', text: raw });
          } else {
            parts.push({ type: 'translation', text: raw });
          }
        }

        let html = `<div class="sentence-block">${badge}`;
        for (const p of parts) {
          switch (p.type) {
            case 'target':
              html += `<div class="sentence-target">${wrapDir(renderInlineBook(p.text, baseLang))}</div>`;
              break;
            case 'rom':
              html += `<div class="sentence-ipa"><span class="rom-tag">ROM</span><em>${wrapDir(escapeHtml(p.text))}</em></div>`;
              break;
            case 'ipa':
              html += `<div class="sentence-ipa"><em>${escapeHtml(p.text)}</em></div>`;
              break;
            case 'glo':
              html += `<div class="sentence-gloss"><span class="gloss-tag">GLOSS</span><em>${wrapDir(escapeHtml(p.text))}</em></div>`;
              break;
            case 'translation':
              html += `<div class="sentence-translation">${wrapDir(renderInlineBook(p.text, baseLang))}</div>`;
              break;
          }
        }
        html += '</div>';
        blocks.push(html);
      }
      continue;
    }

    // Mini-dialog turn. Two source formats are supported:
    //   "A [POL]: <target>"     (speaker, tag, colon after the tag)
    //   "**A:** [POL] <target>"  (bold speaker carrying its own colon, tag, no trailing colon)
    // each followed by [ROM]/[GLO] and an English translation line on their own lines.
    // (Distinct from sentence blocks, which start with the tag and have no speaker.)
    const TURN_RE = /^\*{0,2}([^\[\]]{1,12}?)(:?)\*{0,2}\s*\[([A-Z]{2,4})\](:?)\s*(.+)$/;
    const matchTurn = (s: string) => {
      const m = s.match(TURN_RE);
      if (!m || !REGISTER_TAGS[m[3]!]) return null;
      // A turn is valid when it carries a colon (speaker: or [TAG]:) OR when the speaker slot is just a
      // dash dialogue marker (—/–/―) — the Russian/European convention "— [TAG] target" (no colon, no
      // named speaker). The marker must be an em/en/bar dash, NOT a hyphen, so markdown bullet lines
      // like "- [CAS] casual register…" are never mistaken for dialogue turns.
      const dashMarker = /^\s*[—–―]\s*$/.test(m[1]!);
      if (m[2] !== ':' && m[4] !== ':' && !dashMarker) return null;
      return { speaker: dashMarker ? '' : m[1]!.replace(/[*:]/g, '').trim(), tag: m[3]!, target: m[5]!.trim() };
    };
    if (matchTurn(trimmed)) {
      const turns: string[] = [];
      while (i < lines.length) {
        const head = (lines[i] ?? '').trim();
        const hmt = matchTurn(head);
        if (!hmt) break;
        const { speaker, tag, target } = hmt;
        i++;
        const cont: string[] = [];
        while (i < lines.length) {
          const cl = (lines[i] ?? '').trim();
          if (cl === '') break;
          if (matchTurn(cl)) break;
          if (/^#{1,6}\s/.test(cl) || cl.startsWith('>') || isTableRow(cl) || /^-{3,}$/.test(cl)) break;
          cont.push(cl);
          i++;
        }
        let rom = '', glo = '';
        const trans: string[] = [];
        // Only non-Latin-script targets (Hebrew, CJK, Cyrillic, Arabic, Devanagari…) carry a
        // romanization line. For those, an untagged first continuation line IS the romanization.
        // Latin-script targets (Italian, Spanish, French…) have no romanization, so an untagged
        // line there is the TRANSLATION — never label it ROM.
        const targetHasNonLatin = /[Ͱ-ϿЀ-ԯ֐-׿؀-ۿ܀-ݏऀ-ॿ฀-๿ᄀ-ᇿ぀-ヿ㐀-鿿가-힯豈-﫿]/.test(target);
        for (const c of cont) {
          if (c.startsWith('[GLO]')) glo = c.replace(/^\[GLO\]\s*/, '').trim();
          else if (c.startsWith('[ROM]')) rom = c.replace(/^\[ROM\]\s*/, '').trim();
          else if (targetHasNonLatin && !rom && !glo && trans.length === 0) rom = c;   // non-Latin target: first untagged line = romanization
          else trans.push(c);
        }
        let t = '<div class="dialog-turn">';
        t += `<div class="dialog-head">${speaker ? `<span class="dialog-speaker">${escapeHtml(speaker)}</span>` : ''}${renderRegisterBadge(tag, baseLang)}</div>`;
        t += `<div class="sentence-target">${wrapDir(renderInlineBook(target, baseLang))}</div>`;
        if (rom) t += `<div class="sentence-ipa"><span class="rom-tag">ROM</span><em>${wrapDir(escapeHtml(rom))}</em></div>`;
        if (glo) t += `<div class="sentence-gloss"><span class="gloss-tag">GLOSS</span><em>${wrapDir(escapeHtml(glo))}</em></div>`;
        if (trans.length) t += `<div class="sentence-translation">${wrapDir(renderInlineBook(trans.join(' '), baseLang))}</div>`;
        t += '</div>';
        turns.push(t);
        while (i < lines.length && (lines[i] ?? '').trim() === '') i++;   // eat blank(s) between turns
      }
      if (turns.length) { blocks.push(`<div class="book-dialog dialog-block">${turns.join('\n')}</div>`); continue; }
    }

    // Paragraph
    const paraLines: string[] = [];
    while (i < lines.length) {
      const pl = (lines[i] ?? '').trim();
      if (pl === '' || pl.startsWith('#') || isTableRow(pl) || /^-{3,}$/.test(pl) || /^[-*+]\s/.test(pl) || /^\d+\.\s/.test(pl) || pl.startsWith('>')) break;
      paraLines.push(pl);
      i++;
    }
    if (paraLines.length > 0) {
      // dir="auto": the paragraph's base direction follows its first strong char (English prose → LTR),
      // and renderInlineBook isolates any embedded Hebrew/Arabic runs. No whole-paragraph RTL wrap.
      blocks.push(`<p class="book-p" dir="auto">${renderInlineBook(paraLines.join(' '), baseLang)}</p>`);
    }
  }

  return blocks.join('\n');
}

export function renderBookCss(): string {
  return `
@import url('https://fonts.googleapis.com/css2?family=Cormorant+Garamond:ital,wght@0,400;0,600;0,700;1,400;1,600&family=DM+Sans:ital,wght@0,400;0,500;0,700;1,400&family=JetBrains+Mono:wght@400;500&display=swap');

:root {
  --color-bg: #faf9f6;
  --color-text: #1a1a1a;
  --color-text-secondary: #4a4a4a;
  --color-accent: #0d6e6e;
  --color-accent-light: #e8f4f4;
  --color-border: #d4d0c8;
  --color-table-header: #f0ede6;
  --color-dialog-bg: #f5f3ee;
  --color-cover-bg: #0d2b3e;
  --color-cover-text: #f0ede6;
  --color-cover-accent: #2dd4bf;
  --color-cover-orange: #ff7a1a;
  /* 'Noto Sans' is the IPA/tone-bar fallback (ʰ ɕ ɛ ɻ ʐ ɯ, ˥˦˧˨˩) — the branded
     faces don't cover those glyphs; the server bundles Noto Sans so they render.
     'Noto Sans Math' covers the mathematical angle brackets ⟨ ⟩ (U+27E8/9) used in
     phonology/orthography notation (⟨s⟩, ⟨c/z⟩), which plain Noto Sans lacks — it is
     in ALL THREE stacks (incl. headings, where phonology subsection titles live). */
  --font-heading: 'Cormorant Garamond', Georgia, 'Times New Roman', 'Noto Sans Math', serif;
  --font-body: 'DM Sans', 'Noto Sans', 'Noto Sans Math', 'Helvetica Neue', Arial, sans-serif;
  --font-mono: 'JetBrains Mono', 'Noto Sans', 'Noto Sans Math', 'Courier New', monospace;
}

* { margin: 0; padding: 0; box-sizing: border-box; }

body {
  font-family: var(--font-body);
  font-size: 11pt;
  line-height: 1.65;
  color: var(--color-text);
  background: var(--color-bg);
}

/* Margins are owned here (pdf.js sets no puppeteer margin): body pages get a margin, the
   cover (page 1) full-bleeds. */
@page { size: A4; margin: 16mm 14mm; }
@page:first { margin: 0; }

/* ── Cover page ─────────────────────────── */
.book-cover {
  min-height: 100vh;
  background: var(--color-cover-bg);
  color: var(--color-cover-text);
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  text-align: center;
  padding: 3rem 2rem;
  page-break-after: always;
  position: relative;
}
.book-cover .cover-logo {
  position: absolute;
  top: 2rem;
  left: 2rem;
  width: 140px;
  color: var(--color-cover-accent);
}
.book-title-page .title-logo { width: 118px; height: auto; margin: 1.5rem 0 2rem; }
.book-cover .cover-image {
  width: 320px;
  max-width: 80%;
  height: auto;
  border-radius: 12px;
  margin-bottom: 2rem;
  /* dark drop shadow + warm orange halo so the image lifts off the dark cover */
  box-shadow: 0 8px 32px rgba(0,0,0,0.4), 0 0 100px 22px rgba(255,122,26,0.42);
}
.book-cover .cover-title {
  font-family: var(--font-heading);
  font-size: 36pt;
  font-weight: 700;
  line-height: 1.15;
  margin-bottom: 0.5rem;
}
.book-cover .cover-subtitle {
  font-family: var(--font-heading);
  font-size: 28pt;
  font-weight: 400;
  color: var(--color-cover-accent);
  margin-bottom: 1.5rem;
}
.book-cover .cover-level {
  display: inline-block;
  font-family: var(--font-body);
  font-size: 26pt;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 3px;
  /* orange highlight so the product type (CEFR level / Audio Course) reads at a glance
     and pops on the store shelf */
  color: #0d2b3e;
  background: var(--color-cover-orange);
  padding: 10px 28px;
  border-radius: 999px;
}

/* ── Title page ─────────────────────────── */
.book-title-page {
  min-height: 100vh;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  text-align: center;
  padding: 3rem 2rem;
  page-break-after: always;
}
.book-title-page h1 {
  font-family: var(--font-heading);
  font-size: 28pt;
  font-weight: 700;
  color: var(--color-text);
  margin-bottom: 0.5rem;
}
.book-title-page .title-subtitle {
  font-family: var(--font-heading);
  font-size: 16pt;
  color: var(--color-accent);
  margin-bottom: 2rem;
}
.book-title-page .title-author {
  font-family: var(--font-body);
  font-size: 12pt;
  color: var(--color-text-secondary);
  margin-bottom: 0.5rem;
}
.book-title-page .title-copyright {
  font-family: var(--font-body);
  font-size: 9pt;
  color: var(--color-text-secondary);
  margin-top: 3rem;
  line-height: 1.6;
}
.book-title-page .title-drm {
  font-family: var(--font-mono);
  font-size: 8pt;
  color: var(--color-text-secondary);
  margin-top: 1.5rem;
  padding: 0.5rem 1rem;
  border: 1px solid var(--color-border);
  border-radius: 4px;
  background: var(--color-accent-light);
}
.book-title-page .title-appstore { margin-top: 2rem; }
.book-title-page .title-appstore-label {
  font-size: 8pt;
  letter-spacing: 0.14em;
  text-transform: uppercase;
  color: var(--color-text-secondary);
  margin-bottom: 0.7rem;
}

/* ── Table of contents ──────────────────── */
.book-toc {
  padding: 3rem 2rem;
  page-break-after: always;
}
.book-toc h2 {
  font-family: var(--font-heading);
  font-size: 22pt;
  font-weight: 700;
  color: var(--color-text);
  margin-bottom: 1.5rem;
  text-align: center;
}
.book-toc .toc-list {
  list-style: none;
  padding: 0;
}
.book-toc .toc-item {
  display: flex;
  align-items: baseline;
  padding: 0.4rem 0;
  border-bottom: 1px dotted var(--color-border);
}
.book-toc .toc-number {
  font-family: var(--font-mono);
  font-size: 10pt;
  color: var(--color-accent);
  min-width: 2rem;
  flex-shrink: 0;
}
.book-toc .toc-title {
  font-family: var(--font-body);
  font-size: 11pt;
  color: var(--color-text);
  flex: 1;
}
.book-toc .toc-title a {
  color: var(--color-text);
  text-decoration: none;
}
.book-toc .toc-title a:hover {
  color: var(--color-accent);
}

/* ── Chapter pages ──────────────────────── */
.book-chapter {
  padding: 2rem;
  page-break-before: always;
}
.book-chapter-header {
  text-align: center;
  margin-bottom: 2rem;
  padding-bottom: 1.5rem;
  border-bottom: 2px solid var(--color-accent);
}
.book-chapter-number {
  font-family: var(--font-mono);
  font-size: 10pt;
  text-transform: uppercase;
  letter-spacing: 3px;
  color: var(--color-accent);
  margin-bottom: 0.5rem;
}
.book-chapter-title {
  font-family: var(--font-heading);
  font-size: 22pt;
  font-weight: 700;
  color: var(--color-text);
}
.book-chapter-image {
  width: 100%;
  max-width: 480px;
  height: auto;
  display: block;
  margin: 1.5rem auto;
  border-radius: 8px;
}

/* ── Section headers ────────────────────── */
.book-section {
  margin-top: 1.5rem;
  margin-bottom: 1rem;
}
.book-section-title {
  font-family: var(--font-heading);
  font-size: 14pt;
  font-weight: 600;
  color: var(--color-accent);
  margin-bottom: 0.75rem;
  padding-bottom: 0.25rem;
  border-bottom: 1px solid var(--color-border);
}

/* ── Content elements ───────────────────── */
.book-p {
  margin-bottom: 0.75rem;
  text-align: justify;
  hyphens: auto;
}
.book-h4 {
  font-family: var(--font-heading);
  font-size: 12pt;
  font-weight: 600;
  color: var(--color-text);
  margin: 1.25rem 0 0.5rem;
}
.book-code {
  font-family: var(--font-mono);
  font-size: 9.5pt;
  background: var(--color-accent-light);
  padding: 0.1em 0.35em;
  border-radius: 3px;
  color: var(--color-accent);
}
.book-table {
  width: 100%;
  border-collapse: collapse;
  margin: 1rem 0;
  font-size: 10pt;
}
.book-table th {
  font-family: var(--font-body);
  font-weight: 600;
  text-align: left;
  padding: 0.4rem 0.6rem;
  background: var(--color-table-header);
  border-bottom: 2px solid var(--color-border);
  color: var(--color-text);
}
.book-table td {
  padding: 0.35rem 0.6rem;
  border-bottom: 1px solid var(--color-border);
  color: var(--color-text-secondary);
  vertical-align: top;
}
.book-table tr:last-child td { border-bottom: none; }
.book-dialog {
  margin: 1rem 0;
  padding: 0.75rem 1rem;
  background: var(--color-dialog-bg);
  border-left: 3px solid var(--color-accent);
  border-radius: 0 6px 6px 0;
  font-size: 10.5pt;
}
.book-dialog p { margin-bottom: 0.3rem; }
.book-ul, .book-ol {
  margin: 0.75rem 0 0.75rem 1.5rem;
  color: var(--color-text-secondary);
}
.book-ul li, .book-ol li { margin-bottom: 0.25rem; }
.book-hr {
  border: none;
  border-top: 1px solid var(--color-border);
  margin: 1.5rem 0;
}

/* ── Register tags ─────────────────────── */
.register-tag {
  display: inline-block;
  font-family: var(--font-mono);
  font-size: 8pt;
  font-weight: 600;
  letter-spacing: 0.5px;
  padding: 0.1em 0.45em;
  border-radius: 3px;
  vertical-align: middle;
  margin-bottom: 0.2rem;
}

/* ── Sentence blocks ───────────────────── */
.sentence-block {
  margin: 0.6rem 0;
  padding: 0.5rem 0.75rem;
  border-left: 3px solid var(--color-accent);
  background: var(--color-accent-light);
  border-radius: 0 6px 6px 0;
}
.sentence-target {
  font-family: var(--font-body);
  font-size: 11pt;
  font-weight: 500;
  color: var(--color-text);
  margin-top: 0.25rem;
}
.sentence-ipa {
  font-family: var(--font-mono);
  font-size: 9pt;
  color: var(--color-text-secondary);
  margin-top: 0.15rem;
}
.sentence-gloss {
  font-family: var(--font-mono);
  font-size: 9pt;
  color: var(--color-text-secondary);
  margin-top: 0.1rem;
}
.gloss-tag, .rom-tag {
  display: inline-block;
  font-family: var(--font-mono);
  font-size: 8pt;
  font-weight: 700;
  letter-spacing: 0.5px;
  padding: 0.1em 0.45em;
  border-radius: 3px;
  vertical-align: middle;
  background: var(--color-accent-light);
  color: var(--color-accent);
  border: 1px solid var(--color-accent);
  margin-right: 0.45em;
}
.sentence-translation {
  font-family: var(--font-body);
  font-size: 10.5pt;
  color: var(--color-text-secondary);
  margin-top: 0.15rem;
  font-style: italic;
}

/* ── Mini-dialogs (speaker turns) ───────── */
.dialog-block { margin: 1rem 0; padding: 0.5rem 0.9rem; background: var(--color-dialog-bg);
  border-left: 3px solid var(--color-accent); border-radius: 0 6px 6px 0; }
.dialog-turn { padding: 0.45rem 0; }
.dialog-turn + .dialog-turn { border-top: 1px solid var(--color-border); }
.dialog-head { display: flex; align-items: center; gap: 0.5rem; margin-bottom: 0.15rem; }
.dialog-speaker { font-family: var(--font-mono); font-size: 9pt; font-weight: 600;
  color: var(--color-accent); min-width: 1.1rem; }
.dialog-turn .sentence-target { margin-top: 0.1rem; }
[dir="rtl"] {
  text-align: right;
  direction: rtl;
  unicode-bidi: isolate;
}

/* ── Page footer (logo + DRM) ───────────── */
.book-page-footer {
  position: fixed;
  bottom: 0;
  left: 0;
  right: 0;
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 0.5rem 2rem;
  font-size: 7pt;
  color: var(--color-text-secondary);
  border-top: 1px solid var(--color-border);
}
.book-page-footer .footer-logo {
  width: 60px;
  color: var(--color-text-secondary);
}
.book-page-footer .footer-drm {
  font-family: var(--font-mono);
  opacity: 0.5;
}

/* ── Print / PDF ────────────────────────── */
@media print {
  body { background: white; }
  .book-cover { min-height: 100vh; }
  .book-chapter { page-break-before: always; }
}

/* ── Preview container (admin) ──────────── */
.book-preview-frame {
  max-width: 800px;
  margin: 0 auto;
  background: var(--color-bg);
  box-shadow: 0 4px 24px rgba(0,0,0,0.3);
  border-radius: 4px;
  overflow: hidden;
}
`;
}

type CoverOverrides = { title?: string; subtitle?: string; hideLevel?: boolean; typeLabel?: string };

function renderCoverHtml(book: ParsedBook, coverImageUrl?: string, overrides: CoverOverrides = {}): string {
  const title = overrides.title ?? bookTitle(book.metadata.languageName, book.metadata.levelName);
  const subtitle = overrides.subtitle !== undefined ? overrides.subtitle : bookSubtitle(book.metadata.levelName);
  // The orange type badge: an explicit typeLabel (e.g. "Audio Course" for companions) wins;
  // otherwise CEFR books show "CEFR <level>". hideLevel with no typeLabel drops it entirely.
  const typeLabel = overrides.typeLabel ?? (overrides.hideLevel ? '' : `CEFR ${book.metadata.level}`);

  return `<div class="book-cover">
  <div class="cover-logo">${GLOSSIKA_LOGO_SVG}</div>
  ${coverImageUrl ? `<img class="cover-image" src="${escapeHtml(coverImageUrl)}" alt="Cover illustration" />` : `<div style="width:320px;height:240px;background:rgba(255,255,255,0.05);border-radius:12px;margin-bottom:2rem;display:flex;align-items:center;justify-content:center;color:rgba(255,255,255,0.2);font-size:14pt;">Cover Image</div>`}
  <div class="cover-title">${escapeHtml(title)}</div>
  ${subtitle ? `<div class="cover-subtitle">${escapeHtml(subtitle)}</div>` : ''}
  ${typeLabel ? `<div class="cover-level">${escapeHtml(typeLabel)}</div>` : ''}
</div>`;
}

// Standalone cover document (the SAME .book-cover design as the CEFR books) for screenshotting
// a product cover. `coverBg` recolours the background — CEFR books use the default navy; the
// audio-course COMPANION passes teal so every cover shares one layout, differing only in colour.
export function renderCoverDoc(opts: {
  coverImageUrl?: string; title?: string; subtitle?: string; hideLevel?: boolean; coverBg?: string; typeLabel?: string;
}): string {
  const css = renderBookCss();
  const bgOverride = opts.coverBg ? `:root{ --color-cover-bg:${opts.coverBg}; }` : '';
  const book = { metadata: { languageName: '', levelName: '', level: '' } } as unknown as ParsedBook;
  const cover = renderCoverHtml(book, opts.coverImageUrl, { title: opts.title, subtitle: opts.subtitle, hideLevel: opts.hideLevel, typeLabel: opts.typeLabel });
  return `<!doctype html><html lang="en"><head><meta charset="utf-8"><style>${css}${bgOverride}</style></head><body>${cover}</body></html>`;
}

// Glossika app download buttons — matches the Blitzscale email-campaign style (slate chrome, 12px label),
// but with the Apple + Google Play logos INLINED as SVG so they render offline in an EPUB / PDF export
// (the campaign markup links external assets/*.svg, which a book cannot fetch). Same block is XHTML-valid,
// so the EPUB builder reuses it verbatim. Links point at the Glossika app (iOS id6667095609 / Android).
const APP_STORE_BUTTONS_HTML = `<div class="title-appstore">
    <div class="title-appstore-label">Get the Glossika app</div>
    <a href="https://apps.apple.com/app/id6667095609" style="display:inline-block;background:#1e293b;border:1px solid #334155;border-radius:8px;padding:6px 12px;text-decoration:none;margin-right:6px;"><svg width="14" height="14" viewBox="0 0 384 512" fill="#cbd5e1" xmlns="http://www.w3.org/2000/svg" style="vertical-align:middle;margin-right:6px;"><path d="M318.7 268.7c-.2-36.7 16.4-64.4 50-84.8-18.8-26.9-47.2-41.7-84.7-44.6-35.5-2.8-74.3 20.7-88.5 20.7-15 0-49.4-19.7-76.4-19.7C63.3 141.2 4 184.8 4 273.5q0 39.3 14.4 81.2c12.8 36.7 59 126.7 107.2 125.2 25.2-.6 43-17.9 75.8-17.9 31.8 0 48.3 17.9 76.4 17.9 48.6-.7 90.4-82.5 102.6-119.3-65.2-30.7-61.7-90-61.7-91.9zm-56.6-164.2c27.3-32.4 24.8-61.9 24-72.5-24.1 1.4-52 16.4-67.9 34.9-17.5 19.8-27.8 44.3-25.6 71.9 26.1 2 49.9-11.4 69.5-34.3z"/></svg><span style="font-size:12px;color:#cbd5e1;font-family:-apple-system,BlinkMacSystemFont,'Segoe UI',Helvetica,Arial,sans-serif;vertical-align:middle;">App Store</span></a><a href="https://play.google.com/store/apps/details?id=com.glossika.video" style="display:inline-block;background:#1e293b;border:1px solid #334155;border-radius:8px;padding:6px 12px;text-decoration:none;"><svg width="14" height="14" viewBox="0 0 512 512" xmlns="http://www.w3.org/2000/svg" style="vertical-align:middle;margin-right:6px;"><path fill="#00e2ff" d="M48.6 26.9C42.9 33 39.6 42.5 39.6 54.8v402.4c0 12.3 3.3 21.8 9.3 27.6l1.3 1.2 225.4-225.4v-5.3L50.2 27.6z"/><path fill="#00d95f" d="m350.5 335.3-75.2-75.2v-5.3l75.2-75.2 1.7 1 89 50.6c25.5 14.4 25.5 38.1 0 52.6l-89 50.5z"/><path fill="#ff3a44" d="m352.2 334.3-76.9-76.9L48.6 484.1c8.4 8.9 22.3 10 38 1.1z"/><path fill="#ffc400" d="M352.2 180.5 86.6 26.8c-15.7-9-29.6-7.8-38 1.1l226.7 226.6z"/></svg><span style="font-size:12px;color:#cbd5e1;font-family:-apple-system,BlinkMacSystemFont,'Segoe UI',Helvetica,Arial,sans-serif;vertical-align:middle;">Google Play</span></a>
  </div>`;

function renderTitlePageHtml(book: ParsedBook, buyerEmail?: string): string {
  const title = bookTitle(book.metadata.languageName, book.metadata.levelName);
  const subtitle = bookSubtitle(book.metadata.levelName);

  return `<div class="book-title-page">
  <h1>${escapeHtml(title)}</h1>
  <div class="title-subtitle">${escapeHtml(subtitle)} &mdash; CEFR ${escapeHtml(book.metadata.level)}</div>
  <img class="title-logo" src="${GLOSSIKA_LOGO.color}" alt="Glossika" />
  <div class="title-author">by Michael Campbell</div>
  <div class="title-copyright">
    &copy; 2026 Glossika Inc. All rights reserved.<br />
    No part of this publication may be reproduced, distributed, or transmitted<br />
    in any form without prior written permission of the publisher.
  </div>
  ${buyerEmail ? `<div class="title-drm">Licensed to: ${escapeHtml(buyerEmail)}</div>` : ''}
  ${APP_STORE_BUTTONS_HTML}
</div>`;
}

function renderTocHtml(book: ParsedBook): string {
  let items = '';
  for (const ch of book.chapters) {
    items += `<li class="toc-item">
      <span class="toc-number">${ch.number}</span>
      <span class="toc-title"><a href="#${ch.id}">${escapeHtml(ch.title)}</a></span>
    </li>\n`;
  }
  return `<div class="book-toc">
  <h2>Contents</h2>
  <ul class="toc-list">${items}</ul>
</div>`;
}

function renderChapterHtml(ch: BookChapter, chapterImageUrl?: string, baseLang?: string): string {
  let body = '';

  if (ch.subsections.length > 0) {
    for (const sub of ch.subsections) {
      body += `<div class="book-section">
        <h3 class="book-section-title">${renderInlineBook(sub.title, baseLang)}</h3>
        ${renderMarkdownToBookHtml(sub.content, baseLang)}
      </div>`;
    }
  } else {
    body = renderMarkdownToBookHtml(ch.content, baseLang);
  }

  // A hero image at the top of the chapter — only for chapters that have one assigned
  // (some chapters, e.g. connectors, intentionally have none).
  const hero = chapterImageUrl
    ? `<img class="book-chapter-image" src="${escapeHtml(chapterImageUrl)}" alt="${escapeHtml(ch.title)}" />`
    : '';

  return `<div class="book-chapter" id="${ch.id}">
  <div class="book-chapter-header">
    <div class="book-chapter-number">Chapter ${ch.number}</div>
    <div class="book-chapter-title">${escapeHtml(ch.title)}</div>
  </div>
  ${hero}
  ${body}
</div>`;
}

export interface RenderOptions {
  coverImageUrl?: string;
  chapterImageUrls?: Record<string, string>;
  buyerEmail?: string;
  previewMode?: boolean;
  baseLang?: string;
}

export function renderBookHtml(book: ParsedBook, options: RenderOptions = {}): string {
  const css = renderBookCss();
  const cover = renderCoverHtml(book, options.coverImageUrl);
  const titlePage = renderTitlePageHtml(book, options.buyerEmail);
  const toc = renderTocHtml(book);

  const baseLang = options.baseLang;

  let chaptersHtml = '';
  if (book.preamble.trim()) {
    chaptersHtml += `<div class="book-chapter" id="ch-preamble">
      <div class="book-chapter-header">
        <div class="book-chapter-number">Introduction</div>
        <div class="book-chapter-title">Language Overview</div>
      </div>
      ${renderMarkdownToBookHtml(book.preamble, baseLang)}
    </div>`;
  }

  for (const ch of book.chapters) {
    const imgUrl = options.chapterImageUrls?.[ch.id];
    chaptersHtml += renderChapterHtml(ch, imgUrl, baseLang);
  }

  const wrapperClass = options.previewMode ? 'book-preview-frame' : '';
  const htmlLang = baseLang || 'en';

  return `<!DOCTYPE html>
<html lang="${htmlLang}">
<head>
  <meta charset="UTF-8" />
  <meta name="viewport" content="width=device-width, initial-scale=1.0" />
  <title>${escapeHtml(bookTitle(book.metadata.languageName, book.metadata.levelName))} — ${escapeHtml(book.metadata.levelName)}</title>
  <style>${css}</style>
</head>
<body>
  <div class="${wrapperClass}">
    ${cover}
    ${titlePage}
    ${toc}
    ${chaptersHtml}
  </div>
</body>
</html>`;
}

export function renderBookPreviewHtml(book: ParsedBook, options: RenderOptions = {}): string {
  return renderBookHtml(book, { ...options, previewMode: true });
}

export { renderMarkdownToBookHtml, renderCoverHtml, renderTitlePageHtml, renderTocHtml, renderChapterHtml };
